"use client";

import { DayPart } from "@/dto/dayPartMenu";
import { useDayPartMenuHandlers } from "./useDayPartMenuHandlers";
import { useCallback } from "react";

interface UseNavigationBarHandlersParams {
  isDineIn: boolean;
  restaurantCartLength: number;
  selectedMenu: DayPart | null;
  searchParams: URLSearchParams | null;
  setIsClearCartOpen: (open: boolean) => void;
  setSelectedMenu?: (menu: DayPart | null) => void;
  isCmaApp: boolean;
  openLoginSignUp: (source: "checkoutButton" | "profileIcon") => void;
  loginModalIsOpen: boolean;
  setLoginModalIsOpen: (open: boolean) => void;
  setIsRetailCartOpen: (open: boolean) => void;
  setToCheckoutAfterLogin: (toCheckout: boolean) => void;
  perksModalIsOpen: boolean;
  setPerksModalIsOpen: (open: boolean) => void;
  hoursModalIsOpen: boolean;
  setHoursModalIsOpen: (open: boolean) => void;
  tempMenu: { current: DayPart | null };
  isUserInitiatedDayPartSwitch: { current: boolean };
  restaurantMfeEnabled?: boolean;
  isDayPartsEnabled?: boolean;
}

export const useNavigationBarHandlers = ({
  isDineIn,
  restaurantCartLength,
  selectedMenu,
  searchParams,
  setIsClearCartOpen,
  setSelectedMenu,
  isCmaApp,
  openLoginSignUp,
  loginModalIsOpen,
  setLoginModalIsOpen,
  setIsRetailCartOpen,
  setToCheckoutAfterLogin,
  perksModalIsOpen,
  setPerksModalIsOpen,
  hoursModalIsOpen,
  setHoursModalIsOpen,
  tempMenu,
  isUserInitiatedDayPartSwitch,
  restaurantMfeEnabled,
  isDayPartsEnabled,
}: UseNavigationBarHandlersParams) => {
  const dayPartHandlers = useDayPartMenuHandlers({
    isDineIn,
    restaurantCartLength,
    selectedMenu,
    searchParams,
    setIsClearCartOpen,
    setSelectedMenu,
    tempMenu,
    isUserInitiatedDayPartSwitch,
    restaurantMfeEnabled,
    isDayPartsEnabled,
  });

  const handleLoginModal = useCallback(
    (toCheckout: boolean) => {
      setIsRetailCartOpen(false);
      setToCheckoutAfterLogin(toCheckout);
      if (isCmaApp) {
        if (!loginModalIsOpen) {
          openLoginSignUp(toCheckout ? "checkoutButton" : "profileIcon");
        }
      } else {
        setLoginModalIsOpen(!loginModalIsOpen);
      }
    },
    [isCmaApp, loginModalIsOpen, openLoginSignUp, setIsRetailCartOpen, setLoginModalIsOpen, setToCheckoutAfterLogin]
  );

  const handlePerksModal = useCallback(
    () => setPerksModalIsOpen(!perksModalIsOpen),
    [perksModalIsOpen, setPerksModalIsOpen]
  );
  const handleHoursModal = useCallback(
    () => setHoursModalIsOpen(!hoursModalIsOpen),
    [hoursModalIsOpen, setHoursModalIsOpen]
  );

  return {
    ...dayPartHandlers,
    handleLoginModal,
    handlePerksModal,
    handleHoursModal,
  };
};
